import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function ProtectionPlans() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: plansRef, getItemStyle } = useStaggerAnimation(3);

  return (
    <section id="protection" className="py-16 lg:py-24 bg-brand-cream">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div
          ref={headerRef}
          className="text-center mb-10 lg:mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="inline-block bg-brand-navy-light text-brand-navy px-4 py-2 rounded-full text-sm font-semibold mb-4">
            PROTECT YOUR INVESTMENT
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4">
            HVAC Protection Plans
          </h2>
          <p className="text-base lg:text-xl text-gray-600 max-w-2xl mx-auto">
            Keep your heating and cooling systems running efficiently with our comprehensive HVAC maintenance plans
          </p>
        </div>

        <div ref={plansRef} className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-8">
          {/* Basic Plan */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-300" style={getItemStyle(0)}>
            <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-gray-100 rounded-2xl mb-4 sm:mb-6">
              <i className="ri-shield-line text-3xl sm:text-4xl text-gray-700"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Basic Plan</h3>
            <div className="mb-4 sm:mb-6">
              <span className="text-3xl sm:text-4xl font-bold text-gray-900">$29</span>
              <span className="text-gray-600">/month</span>
            </div>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Annual HVAC tune-up</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Filter replacement (2x/year)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">System performance check</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">10% discount on repairs</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Priority scheduling</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">24/7 emergency support</span>
              </li>
            </ul>
            <a
              href="#contact"
              className="w-full block text-center border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold hover:border-brand-red hover:text-brand-red transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Select Plan
            </a>
          </div>

          {/* Premium Plan - Highlighted */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border-2 border-brand-red relative md:scale-105 mt-6 md:mt-0" style={getItemStyle(1)}>
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-red text-white px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap">
              RECOMMENDED
            </div>
            <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-brand-red-light rounded-2xl mb-4 sm:mb-6">
              <i className="ri-shield-star-line text-3xl sm:text-4xl text-brand-red"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Premium Plan</h3>
            <div className="mb-4 sm:mb-6">
              <span className="text-3xl sm:text-4xl font-bold text-brand-red">$49</span>
              <span className="text-gray-600">/month</span>
            </div>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Bi-annual HVAC tune-ups</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Filter replacement (4x/year)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Thermostat calibration</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Ductwork inspection</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">20% discount on repairs</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Priority scheduling</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">24/7 emergency support</span>
              </li>
            </ul>
            <a
              href="#contact"
              className="w-full block text-center bg-brand-red text-white px-6 py-3 rounded-full font-semibold hover:bg-brand-red-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Select Plan
            </a>
          </div>

          {/* Elite Plan */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-300" style={getItemStyle(2)}>
            <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-gray-100 rounded-2xl mb-4 sm:mb-6">
              <i className="ri-vip-crown-line text-3xl sm:text-4xl text-gray-700"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Elite Plan</h3>
            <div className="mb-4 sm:mb-6">
              <span className="text-3xl sm:text-4xl font-bold text-gray-900">$79</span>
              <span className="text-gray-600">/month</span>
            </div>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Quarterly HVAC tune-ups</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Filter replacement (6x/year)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Refrigerant level check</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Coil cleaning service</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">Ductwork cleaning (annual)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">30% discount on repairs</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">VIP priority scheduling</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-brand-red text-xl mt-0.5"></i>
                <span className="text-gray-700 text-sm">24/7 emergency support</span>
              </li>
            </ul>
            <a
              href="#contact"
              className="w-full block text-center border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold hover:border-brand-red hover:text-brand-red transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Select Plan
            </a>
          </div>
        </div>

        <p className="text-center text-sm text-gray-600">
          All plans include 24/7 emergency support and priority scheduling for HVAC services
        </p>
      </div>
    </section>
  );
}
